import React, { useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Select, DatePicker, Tabs } from 'antd';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DashboardOutlined,
  UserOutlined
} from '@ant-design/icons';
import useLeadStore from '../store/leadStore';
import useDealStore from '../store/dealStore';
import SalesPersonAnalytics from '../components/SalesPersonAnalytics';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AnalyticsPage = () => {
  const { stats: leadStats, fetchLeadStats } = useLeadStore();
  const { stats: dealStats, fetchDealStats, pipeline, fetchPipeline } = useDealStore();

  useEffect(() => {
    fetchLeadStats();
    fetchDealStats();
    fetchPipeline();
  }, [fetchLeadStats, fetchDealStats, fetchPipeline]);

  // Use real data from API instead of sample data
  const leadTrendData = leadStats?.monthlyTrends || [
    { month: 'Jan', leads: 45, converted: 12 },
    { month: 'Feb', leads: 52, converted: 15 },
    { month: 'Mar', leads: 48, converted: 18 },
    { month: 'Apr', leads: 61, converted: 22 },
    { month: 'May', leads: 55, converted: 19 },
    { month: 'Jun', leads: 67, converted: 25 },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const dealPipelineData = pipeline?.map(stage => ({
    name: stage._id.replace('_', ' ').toUpperCase(),
    value: stage.totalValue,
    count: stage.count
  })) || [];

  const sourceData = leadStats?.sourceBreakdown?.map((source, index) => ({
    name: source._id ? source._id.replace('_', ' ').toUpperCase() : 'Unknown',
    value: source.count,
    color: COLORS[index % COLORS.length]
  })) || [
    { name: 'Website', value: 35, color: '#0088FE' },
    { name: 'Referral', value: 25, color: '#00C49F' },
    { name: 'Social Media', value: 20, color: '#FFBB28' },
    { name: 'Email Campaign', value: 15, color: '#FF8042' },
    { name: 'Other', value: 5, color: '#8884D8' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Title level={2} className="mb-2">Analytics</Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Insights and performance metrics for your CRM
          </Text>
        </div>
        <div className="flex space-x-4">
          <Select defaultValue="30" style={{ width: 120 }}>
            <Select.Option value="7">Last 7 days</Select.Option>
            <Select.Option value="30">Last 30 days</Select.Option>
            <Select.Option value="90">Last 90 days</Select.Option>
            <Select.Option value="365">Last year</Select.Option>
          </Select>
          <RangePicker />
        </div>
      </div>

      <Tabs 
        defaultActiveKey="overview" 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-soft p-6"
        items={[
          {
            key: 'overview',
            label: (
              <span>
                <DashboardOutlined />
                Overview
              </span>
            ),
            children: (
              <div>
                {/* Key Metrics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Total Revenue"
              value={dealStats?.totalRevenue || 0}
              prefix="$"
              precision={0}
              valueStyle={{ color: '#3f8600' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Monthly Growth: {dealStats?.revenueGrowth > 0 ? '+' : ''}{dealStats?.revenueGrowth || 0}%
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Win Rate"
              value={dealStats?.winRate || 0}
              suffix="%"
              valueStyle={{ color: '#cf1322' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Won: {dealStats?.wonDeals || 0} / Total: {dealStats?.totalDeals || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Avg Deal Size"
              value={dealStats?.avgDealSize || 0}
              prefix="$"
              precision={0}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Cycle Time: {Math.round(dealStats?.avgCycleTime || 0)} days
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Conversion Rate"
              value={leadStats?.conversionRate || 0}
              suffix="%"
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Avg Response: {Math.round(leadStats?.avgResponseTime || 0)}h
            </div>
          </Card>
        </Col>
      </Row>

      {/* Additional Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Active Leads"
              value={leadStats?.activeLeads || 0}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Monthly Growth: {leadStats?.monthlyGrowth > 0 ? '+' : ''}{leadStats?.monthlyGrowth || 0}%
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Active Deals"
              value={dealStats?.activeDeals || 0}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Closing Soon: {dealStats?.closingSoon || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Pipeline Value"
              value={dealStats?.pipelineValue?.weightedValue || 0}
              prefix="$"
              precision={0}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Total: ${(dealStats?.pipelineValue?.totalValue || 0).toLocaleString()}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="This Month Revenue"
              value={dealStats?.thisMonthRevenue || 0}
              prefix="$"
              precision={0}
              valueStyle={{ color: '#fa8c16' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Last Month: ${(dealStats?.lastMonthRevenue || 0).toLocaleString()}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]}>
        {/* Lead Trends */}
        <Col xs={24} lg={12}>
          <Card title="Lead Generation Trends" className="shadow-soft">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={leadTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="leads" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  name="Total Leads"
                />
                <Line 
                  type="monotone" 
                  dataKey="converted" 
                  stroke="#82ca9d" 
                  strokeWidth={2}
                  name="Converted"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Lead Sources */}
        <Col xs={24} lg={12}>
          <Card title="Lead Sources" className="shadow-soft">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sourceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sourceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Deal Pipeline */}
        <Col xs={24} lg={16}>
          <Card title="Deal Pipeline by Stage" className="shadow-soft">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dealPipelineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'value' ? `$${value.toLocaleString()}` : value,
                    name === 'value' ? 'Total Value' : 'Deal Count'
                  ]}
                />
                <Legend />
                <Bar dataKey="value" fill="#8884d8" name="Total Value" />
                <Bar dataKey="count" fill="#82ca9d" name="Deal Count" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Performance Summary */}
        <Col xs={24} lg={8}>
          <Card title="Performance Summary" className="shadow-soft">
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <Text className="block font-medium">This Month Leads</Text>
                  <Text className="text-sm text-gray-500">New leads this month</Text>
                </div>
                <Text className="text-2xl font-bold text-blue-600">
                  {leadStats?.thisMonthLeads || 0}
                </Text>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <Text className="block font-medium">This Month Deals</Text>
                  <Text className="text-sm text-gray-500">New deals this month</Text>
                </div>
                <Text className="text-2xl font-bold text-green-600">
                  {dealStats?.thisMonthDeals || 0}
                </Text>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <Text className="block font-medium">Avg Lead Value</Text>
                  <Text className="text-sm text-gray-500">Average estimated value</Text>
                </div>
                <Text className="text-2xl font-bold text-purple-600">
                  ${Math.round(leadStats?.avgLeadValue || 0).toLocaleString()}
                </Text>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <Text className="block font-medium">Avg Deal Cycle</Text>
                  <Text className="text-sm text-gray-500">Days to close</Text>
                </div>
                <Text className="text-2xl font-bold text-orange-600">
                  {Math.round(dealStats?.avgCycleTime || 0)}
                </Text>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <Text className="block font-medium">Recent Activity</Text>
                  <Text className="text-sm text-gray-500">Last 30 days</Text>
                </div>
                <Text className="text-2xl font-bold text-cyan-600">
                  {(leadStats?.recentLeads || 0) + (dealStats?.recentDeals || 0)}
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Monthly Performance */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Lead Performance Trends" className="shadow-soft">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={leadTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="leads"
                  stackId="1"
                  stroke="#8884d8"
                  fill="#8884d8"
                  fillOpacity={0.6}
                  name="Total Leads"
                />
                <Area
                  type="monotone"
                  dataKey="converted"
                  stackId="2"
                  stroke="#82ca9d"
                  fill="#82ca9d"
                  fillOpacity={0.6}
                  name="Converted Leads"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Deal Revenue Trends" className="shadow-soft">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dealStats?.monthlyTrends || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    name.includes('Value') ? `$${value.toLocaleString()}` : value,
                    name
                  ]}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="totalValue"
                  stackId="1"
                  stroke="#1890ff"
                  fill="#1890ff"
                  fillOpacity={0.6}
                  name="Total Deal Value"
                />
                <Area
                  type="monotone"
                  dataKey="wonValue"
                  stackId="2"
                  stroke="#52c41a"
                  fill="#52c41a"
                  fillOpacity={0.6}
                  name="Won Deal Value"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Additional Analytics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Lead Status Breakdown" className="shadow-soft">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={leadStats?.statusBreakdown?.map((status, index) => ({
                    name: status._id ? status._id.replace('_', ' ').toUpperCase() : 'Unknown',
                    value: status.count,
                    fill: COLORS[index % COLORS.length]
                  })) || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(leadStats?.statusBreakdown || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Deal Priority Distribution" className="shadow-soft">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dealStats?.priorityBreakdown?.map(priority => ({
                name: priority._id ? priority._id.replace('_', ' ').toUpperCase() : 'Unknown',
                count: priority.count,
                value: priority.totalValue
              })) || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'value' ? `$${value.toLocaleString()}` : value,
                    name === 'value' ? 'Total Value' : 'Deal Count'
                  ]}
                />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" name="Deal Count" />
                <Bar dataKey="value" fill="#82ca9d" name="Total Value" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
                </Row>
              </div>
            )
          },
          {
            key: 'salesperson',
            label: (
              <span>
                <UserOutlined />
                Sales Person
              </span>
            ),
            children: <SalesPersonAnalytics />
          }
        ]}
      />
    </div>
  );
};

export default AnalyticsPage;
